import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Label, TextInput, Textarea } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import SkyClear from "../../../../../img/SkyClear.png";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { PadresHermanosInterface } from "../../../../../interfaces/PadresHermanosInterface";
import { ConyugeInterface } from "../../../../../interfaces/ConyugeInterface";
import { HijosInterface } from "../../../../../interfaces/HijosInterface";
import { NotasInfofamInterface } from "../../../../../interfaces/NotasInfofamInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import { InfoFamiliarPrint } from "./InfoFamiliarPrint";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const InfoFamiliarListaScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "INFORMACIÓN FAMILIAR";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [referenciasLaborales, setReferenciasLaborales] = useState([
    {
      nombre: "",
      parentesco: "",
      edadcivil: "",
      domicilio: "",
      viveconeva: "",
      actividadlabo: "",
      ultimogrado: "",
      notes: "",
      edad: "",
    },
  ]);
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [edad, setEdad] = useState<number | null>(null);
  const { control, handleSubmit, setValue, reset } = useForm();
  const [formData, setFormData] = useState({
    visitDate: "",
    name: "",
  });

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReferenciaChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setReferenciasLaborales((prev) =>
      prev.map((referencia, i) =>
        i === index ? { ...referencia, [name]: value } : referencia
      )
    );
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const handlePrint = () => {
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  const renderHeader = () => (
    <>
      {/* Header */}
      <Header titulo={tituloFormulario} />
    </>
  );

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFecha = useGetData<FechasInterface[]>({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    name: ["fecha-visita-bienesinmuebles"],
  });

  const queryPadresHermanos = useGetData<PadresHermanosInterface[]>({
    url: `/padres-hermanos/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "padres-hermanos"],
  });

  const queryConyuge = useGetData<ConyugeInterface[]>({
    url: `/conyuge/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "conyuge"],
  });

  const queryHijos = useGetData<HijosInterface>({
    url: `/hijos/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "hijos"],
  });

  const queryNotasInfoFam = useGetData<NotasInfofamInterface[]>({
    url: `/notas_infofam/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "notas_infofam"],
  });

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: fechaNac,
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
        puesto: queryEvaluado.data.puesto || "",
        fecha_ingreso: queryEvaluado.data.fecha_ingreso || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    if (queryPadresHermanos.data && queryPadresHermanos.data.length > 0) {
      const ph = queryPadresHermanos.data[0];
      reset({
        nombre_ph: ph.nombre_ph1 || "",
        parentesco_ph: ph.parentesco_ph1 || "",
        edad_ph: ph.edad_ph1 || "",
        estado_civilph: ph.estado_civilph1 || "",
        domicilio_ph: ph.domicilio_ph1 || "",
        vivecon_evaluadoph: ph.vivecon_evaluadoph1 || "",
        actividad_laboralph: ph.actividad_laboralph1 || "",
        ultimogrado_estudiosph: ph.ultimogrado_estudiosph1 || "",
      });
    }
  }, [queryPadresHermanos.data, reset]);

  useEffect(() => {
    if (queryConyuge.data && queryConyuge.data.length > 0) {
      const conyuge = queryConyuge.data[0];
      reset({
        nombrec: conyuge.nombrec || "",
        edadc: conyuge.edadc || "",
        domicilioc: conyuge.domicilioc || "",
        vivecon_evaluadoc: conyuge.vivecon_evaluadoc || "",
        actividad_laboralc: conyuge.actividad_laboralc || "",
        ultimogrado_estudiosc: conyuge.ultimogrado_estudiosc || "",
      });
    }
  }, [queryConyuge.data, reset]);

  useEffect(() => {
    if (queryHijos.data) {
      const hijo = queryHijos.data;
      reset({
        nombreh1: hijo.nombreh1 || "",
        edadh1: hijo.edadh1 || "",
        domicilioh1: hijo.domicilioh1 || "",
        vivecon_evaluadoh1: hijo.vivecon_evaluadoh1 || "",
        actividad_laboralh1: hijo.actividad_laboralh1 || "",
        ultimogrado_estudiosh1: hijo.ultimogrado_estudiosh1 || "",
        nombreh2: hijo.nombreh2 || "",
        edadh2: hijo.edadh2 || "",
        domicilioh2: hijo.domicilioh2 || "",
        vivecon_evaluadoh2: hijo.vivecon_evaluadoh2 || "",
        actividad_laboralh2: hijo.actividad_laboralh2 || "",
        ultimogrado_estudiosh2: hijo.ultimogrado_estudiosh2 || "",
        nombreh3: hijo.nombreh3 || "",
        edadh3: hijo.edadh3 || "",
        domicilioh3: hijo.domicilioh3 || "",
        vivecon_evaluadoh3: hijo.vivecon_evaluadoh3 || "",
        actividad_laboralh3: hijo.actividad_laboralh3 || "",
        ultimogrado_estudiosh3: hijo.ultimogrado_estudiosh3 || "",
        nombreh4: hijo.nombreh4 || "",
        edadh4: hijo.edadh4 || "",
        domicilioh4: hijo.domicilioh4 || "",
        vivecon_evaluadoh4: hijo.vivecon_evaluadoh4 || "",
        actividad_laboralh4: hijo.actividad_laboralh4 || "",
        ultimogrado_estudiosh4: hijo.ultimogrado_estudiosh4 || "",
      });
    }
  }, [queryHijos.data, reset]);

  useEffect(() => {
    if (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0) {
      const notasinfo = queryNotasInfoFam.data[0];
      reset({
        nota_ph: notasinfo.nota_ph || "",
        nota_c: notasinfo.nota_c || "",
        nota_h: notasinfo.nota_h || "",
        nota_f: notasinfo.nota_f || "",
      });
    }
  }, [queryNotasInfoFam.data, reset]);

  useEffect(() => {
    if (queryFecha.data && queryFecha.data.length > 0) {
      const registroValido = queryFecha.data.find((item) => item.fecha_infofam);

      if (registroValido) {
        setValue("fecha_infofam", registroValido.fecha_infofam);
      }
    }
  }, [queryFecha.data, setValue]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <Card className={`mt-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <form className={`flex flex-col gap-4`}>
        {/* Fecha Visita */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="visitDate" value="FECHA VISITA:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <Controller
              name="fecha_infofam"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextInput
                  id="fecha_infofam"
                  name="fecha_infofam"
                  type="date"
                  value={
                    queryFecha.data && queryFecha.data.length > 0
                      ? queryFecha.data[0].fecha_infofam || ""
                      : ""
                  }
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
          {/* Campo para nombre */}
          <div className="col-span-2">
            <Label htmlFor="name" value="NOMBRE:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="name"
              name="name"
              value={`${queryEvaluado.data?.nombre || ""} ${
                queryEvaluado.data?.apellido_paterno || ""
              } ${queryEvaluado.data?.apellido_materno || ""}`}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
        </div>
        {/* Campo para RFC */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="rfc" value="RFC:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="rfc"
              name="rfc"
              value={queryEvaluado.data?.rfc || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Edad */}
          <div className="col-span-1">
            <Label htmlFor="edad" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>EDAD:</Label>
            <TextInput
              id="edad"
              name="edad"
              value={edad !== null ? edad.toString() : ""}
              disabled={true}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Género */}
          <div>
            <Label htmlFor="sex" value="SEXO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="sex"
              name="sex"
              value={generoTexto}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Estado Civil */}
          <div>
            <Label htmlFor="civilStatus" value="ESTADO CIVIL:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="civilStatus"
              name="civilStatus"
              value={queryEvaluado.data?.estado_civil || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Escolaridad */}
          <div>
            <Label htmlFor="education" value="ESCOLARIDAD:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="education"
              name="education"
              value={queryEvaluado.data?.escolaridad || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Fecha de Nacimiento */}
          <div>
            <Label htmlFor="birthDate" value="FECHA DE NACIMIENTO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="birthDate"
              name="birthDate"
              type="date"
              value={queryEvaluado.data?.fecha_nacimiento || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* CURP */}
          <div>
            <Label htmlFor="curp" value="CURP:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <TextInput
              id="curp"
              name="curp"
              value={queryEvaluado.data?.curp || ""}
              disabled={true}
              onChange={handleInputChange}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
          {/* Empresa */}
          <div className="col-span-1">
            <Label htmlFor="empresa" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}>EMPRESA:</Label>
            <TextInput
              id="empresa_id"
              name="empresa"
              value={queryEvaluado.data?.empresa_id?.nombre || ""}
              onChange={handleInputChange}
              disabled={true}
              className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
            />
          </div>
        </div>
      </form>
    </Card>
  );

  const renderPage2 = (index: number) => (
    <Card className={`mt-4 shadow-lg border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}>
      <h2 className={`text-xl font-bold mb-4 p-2 rounded-t ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
        PADRES Y HERMANOS {index + 1}
      </h2>
      {[...Array(5)].map((_, index) => (
        <div key={index} className={`space-y-4 p-4 rounded-b ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
          <div className="flex flex-col text-left">
            <Label
              htmlFor={`nombre_ph${index + 1}`}
              value="NOMBRE:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`nombre_ph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `nombre_ph${index + 1}` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`nombre_ph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`parentesco_ph${index + 1}`}
              value="PARENTESCO:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`parentesco_ph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `parentesco_ph${
                        index + 1
                      }` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`parentesco_ph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`edad_ph${index + 1}`}
              value="EDAD:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`edad_ph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `edad_ph${index + 1}` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`edad_ph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`estado_civilph${index + 1}`}
              value="ESTADO CIVIL:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`estado_civilph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `estado_civilph${
                        index + 1
                      }` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`estado_civilph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`domicilio_ph${index + 1}`}
              value="DOMICILIO:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`domicilio_ph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `domicilio_ph${
                        index + 1
                      }` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`domicilio_ph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`vivecon_evaluadoph${index + 1}`}
              value="VIVE CON EL EVALUADO:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`vivecon_evaluadoph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `vivecon_evaluadoph${
                        index + 1
                      }` as keyof PadresHermanosInterface
                    ]?.toString() || "false"
                  : "false"
              }
              render={({ field }) => (
                <select
                  id={`vivecon_evaluadoph${index + 1}`}
                  {...field}
                  onChange={(e) => field.onChange(e.target.value === "true")}
                  value={field.value.toString()}
                  className={`border rounded-md p-2 w-full ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  disabled={true}
                >
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`actividad_laboralph${index + 1}`}
              value="ACTIVIDAD LABORAL:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`actividad_laboralph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `actividad_laboralph${
                        index + 1
                      }` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`actividad_laboralph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>

          <div className="flex flex-col text-left">
            <Label
              htmlFor={`ultimogrado_estudiosph${index + 1}`}
              value="ÚLTIMO GRADO DE ESTUDIOS:"
              className={`text-left font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`ultimogrado_estudiosph${index + 1}`}
              control={control}
              defaultValue={
                queryPadresHermanos.data && queryPadresHermanos.data.length > 0
                  ? queryPadresHermanos.data[0][
                      `ultimogrado_estudiosph${
                        index + 1
                      }` as keyof PadresHermanosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`ultimogrado_estudiosph${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
        </div>
      ))}
    </Card>
  );

  const renderPage3 = (index: number) => (
    <Card className={`mt-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <h2 className={`text-xl font-bold mb-4 p-2 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
        CÓNYUGE{" "}
      </h2>
      <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        <div className="flex flex-col text-left">
          <Label htmlFor="nombrec" value="NOMBRE:" className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <Controller
            name="nombrec"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <input
                type="text"
                id="nombrec"
                value={
                  field.value ||
                  (queryConyuge.data && queryConyuge.data.length > 0
                    ? queryConyuge.data[0]?.nombrec || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => field.onChange(e.target.value)}
                className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label htmlFor="edadc" value="EDAD:" className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <Controller
            name="edadc"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <input
                type="text"
                id="edadc"
                value={
                  field.value ||
                  (queryConyuge.data && queryConyuge.data.length > 0
                    ? queryConyuge.data[0]?.edadc || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => field.onChange(e.target.value)}
                className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor="domicilioc"
            value="DOMICILIO:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name="domicilioc"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <input
                type="text"
                id="domicilioc"
                value={
                  field.value ||
                  (queryConyuge.data && queryConyuge.data.length > 0
                    ? queryConyuge.data[0]?.domicilioc || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => field.onChange(e.target.value)}
                className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor="vivecon_evaluadoc"
            value="VIVE CON EL EVALUADO:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name="vivecon_evaluadoc"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <select
                id="vivecon_evaluadoc"
                value={
                  field.value ||
                  (queryConyuge.data && queryConyuge.data.length > 0
                    ? queryConyuge.data[0]?.vivecon_evaluadoc || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => field.onChange(e.target.value)}
                className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              >
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor="actividad_laboralc"
            value="ACTIVIDAD LABORAL:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name="actividad_laboralc"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <input
                type="text"
                id="actividad_laboralc"
                value={
                  field.value ||
                  (queryConyuge.data && queryConyuge.data.length > 0
                    ? queryConyuge.data[0]?.actividad_laboralc || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => field.onChange(e.target.value)}
                className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              />
            )}
          />
        </div>
        <div className="flex flex-col text-left">
          <Label
            htmlFor="ultimogrado"
            value="ÚLTIMO GRADO DE ESTUDIOS:"
            className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
          />
          <Controller
            name="ultimogrado_estudiosc"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <input
                type="text"
                id="ultimogrado_estudiosc"
                value={
                  field.value ||
                  (queryConyuge.data && queryConyuge.data.length > 0
                    ? queryConyuge.data[0]?.ultimogrado_estudiosc || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => field.onChange(e.target.value)}
                className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
              />
            )}
          />
        </div>
        <h2 className={`text-xl font-bold mb-4 p-2 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
          NOTAS
        </h2>
        <div className="space-y-4">
          <Controller
            name="nota_c"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Textarea
                id="nota_c"
                name="nota_c"
                value={
                  field.value ||
                  (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0
                    ? queryNotasInfoFam.data[0]?.nota_c || ""
                    : "")
                }
                disabled={true}
                onChange={(e) => handleReferenciaChange(index, e)}
                rows={6}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                className={`mt-2 p-2 border rounded-md resize-none ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                style={{ height: "auto", overflow: "hidden" }}
              />
            )}
          />
        </div>
        <div className={`mt-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          <p className="text-left">
            {" "}
            Bajo protesta de decir verdad, hago constar que los datos
            proporcionados son verdaderos.{" "}
          </p>
          <p className="text-left">
            Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A. de
            C.V. para que proporcione toda la Información, documentos y todos
            los datos que derive de las evaluaciones de pruebas de confianza que
            se me realicen.{" "}
          </p>
        </div>
      </div>
    </Card>
  );

  const renderPage4 = (index: number) => (
    <Card className={`mt-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <h2 className={`text-xl font-bold mb-4 p-2 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
        Hijos {index + 1}{" "}
      </h2>
      {[...Array(4)].map((_, index) => (
        <div key={index} className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
          <div className="flex flex-col text-left">
            <Label htmlFor={`nombreh${index + 1}`} value="NOMBRE:" className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <Controller
              name={`nombreh${index + 1}`}
              control={control}
              defaultValue={
                queryHijos.data
                  ? queryHijos.data[
                      `nombreh${index + 1}` as keyof HijosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`nombreh${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
          <div className="flex flex-col text-left">
            <Label htmlFor={`edadh${index + 1}`} value="EDAD:" className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
            <Controller
              name={`edadh${index + 1}`}
              control={control}
              defaultValue={
                queryHijos.data
                  ? queryHijos.data[
                      `edadh${index + 1}` as keyof HijosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`edadh${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
          <div className="flex flex-col text-left">
            <Label
              htmlFor={`domicilioh${index + 1}`}
              value="DOMICILIO:"
              className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`domicilioh${index + 1}`}
              control={control}
              defaultValue={
                queryHijos.data
                  ? queryHijos.data[
                      `domicilioh${index + 1}` as keyof HijosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`domicilioh${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
          <div className="flex flex-col text-left">
            <Label
              htmlFor={`vivecon_evaluadoh${index + 1}`}
              value="VIVE CON EL EVALUADO:"
              className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`vivecon_evaluadoh${index + 1}`}
              control={control}
              defaultValue={
                queryHijos.data
                  ? queryHijos.data[
                      `vivecon_evaluadoh${index + 1}` as keyof HijosInterface
                    ]?.toString() || "null"
                  : "null"
              }
              render={({ field }) => (
                <select
                  id={`vivecon_evaluadoh${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`border rounded-md p-2 w-full ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                >
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              )}
            />
          </div>
          <div className="flex flex-col text-left">
            <Label
              htmlFor={`actividad_laboralh${index + 1}`}
              value="ACTIVIDAD LABORAL:"
              className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`actividad_laboralh${index + 1}`}
              control={control}
              defaultValue={
                queryHijos.data
                  ? queryHijos.data[
                      `actividad_laboralh${index + 1}` as keyof HijosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`actividad_laboralh${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
          <div className="flex flex-col text-left">
            <Label
              htmlFor={`ultimogrado_estudiosh${index + 1}`}
              value="ÚLTIMO GRADO DE ESTUDIOS:"
              className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
            />
            <Controller
              name={`ultimogrado_estudiosh${index + 1}`}
              control={control}
              defaultValue={
                queryHijos.data
                  ? queryHijos.data[
                      `ultimogrado_estudiosh${
                        index + 1
                      }` as keyof HijosInterface
                    ] || ""
                  : ""
              }
              render={({ field }) => (
                <TextInput
                  id={`ultimogrado_estudiosh${index + 1}`}
                  {...field}
                  disabled={true}
                  className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
                />
              )}
            />
          </div>
        </div>
      ))}
    </Card>
  );

  const renderPage5 = (index: number) => (
    <Card className={`mt-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      <h2 className={`text-xl font-bold mb-4 p-2 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
        NOTAS
      </h2>
      <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        <Controller
          name="nota_f"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Textarea
              id="nota_f"
              name="nota_f"
              value={
                field.value ||
                (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0
                  ? queryNotasInfoFam.data[0]?.nota_f || ""
                  : "")
              }
              disabled={true}
              onChange={(e) => handleReferenciaChange(index, e)}
              rows={6}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                target.style.height = `${target.scrollHeight}px`;
              }}
              className={`mt-2 p-2 border rounded-md resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "bg-gray-100 text-gray-800"}`}
              style={{ height: "auto", overflow: "hidden" }}
            />
          )}
        />
      </div>
      <div className={`mt-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
        <p className="text-left">
          {" "}
          Bajo protesta de decir verdad, hago constar que los datos
          proporcionados son verdaderos.{" "}
        </p>
        <p className="text-left">
          Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A. de C.V.
          para que proporcione toda la Información, documentos y todos los datos
          que derive de las evaluaciones de pruebas de confianza que se me
          realicen.{" "}
        </p>
      </div>
    </Card>
  );

  const addReferenciaLaboral = () => {
    setReferenciasLaborales([
      ...referenciasLaborales,
      {
        nombre: "",
        parentesco: "",
        edadcivil: "",
        domicilio: "",
        viveconeva: "",
        actividadlabo: "",
        ultimogrado: "",
        notes: "",
        edad: "",
      },
    ]);
  };

  const validateAndRemoveEmptyReferences = () => {
    setReferenciasLaborales((prev) =>
      prev.filter((referencia, index) => {
        if (index === 0) return true;
        const isFilled = Object.values(referencia).some(
          (value) => value.trim() !== ""
        );
        return isFilled;
      })
    );
  };

  const handlePageChange = (direction: "next" | "prev") => {
    validateAndRemoveEmptyReferences();
    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === "prev") {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
          {renderHeader()}
          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage2(index)}
                </div>
              ))}
              <h2 className={`text-xl font-bold mb-4 p-2 rounded-t ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
                NOTAS
              </h2>
              <div className="space-y-4 m-4">
                <Controller
                  name="nota_ph"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Textarea
                      id="nota_ph"
                      {...field}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = "auto";
                        target.style.height = `${target.scrollHeight}px`;
                      }}
                      className={`mt-2 p-2 border rounded-md resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"}`}
                      style={{ height: "auto", overflow: "hidden" }}
                    />
                  )}
                />
              </div>
              <div className="flex justify-center mt-4">
                <div className="w-full max-w-4xl px-4">
                  <div className={`mt-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    <p className="text-left">
                      Bajo protesta de decir verdad, hago constar que los datos
                      proporcionados son verdaderos.
                    </p>
                    <p className="text-left">
                      Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY
                      S.A. de C.V. para que proporcione toda la información,
                      documentos y todos los datos que derive de las evaluaciones
                      de pruebas de confianza que se me realicen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentPage === 3 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage3(index)}
                </div>
              ))}
            </div>
          )}

          {currentPage === 4 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage4(index)}
                </div>
              ))}
              <h2 className={`text-xl font-bold mb-4 p-2 rounded-t ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
                NOTAS
              </h2>
              <div className="space-y-4 m-4">
                <Controller
                  name="nota_h"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Textarea
                      id="nota_h"
                      {...field}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = "auto";
                        target.style.height = `${target.scrollHeight}px`;
                      }}
                      className={`mt-2 p-2 border rounded-md resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"}`}
                      style={{ height: "auto", overflow: "hidden" }}
                    />
                  )}
                />
              </div>
              <div className={`m-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                <p className="text-left">
                  {" "}
                  Bajo protesta de decir verdad, hago constar que los datos
                  proporcionados son verdaderos.{" "}
                </p>
                <p className="text-left">
                  Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A.
                  de C.V. para que proporcione toda la Información, documentos y
                  todos los datos que derive de las evaluaciones de pruebas de
                  confianza que se me realicen.{" "}
                </p>
              </div>
            </div>
          )}

          {currentPage === 5 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage5(index)}
                </div>
              ))}
            </div>
          )}

          {/* Botones de navegación */}
          <div className="flex justify-between mt-4 px-4 mb-4">
            {currentPage < 2 && (
              <Button
                color="red"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                }}
                className={`${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
              >
                Cancelar
              </Button>
            )}
            {currentPage > 1 && (
              <Button
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("prev");
                }}
                className={`${isDarkMode ? "bg-violet-600 hover:bg-violet-900 text-white" : "bg-violet-500 text-white"}`}
              >
                Anterior
              </Button>
            )}
            {currentPage < 5 ? (
              <Button
                color="blue"
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("next");
                }}
                className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
              >
                Siguiente
              </Button>
            ) : (
              <div className="flex space-x-4">
                <Button color="blue" onClick={handlePrint} className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}>
                  Imprimir
                </Button>
                <button
                  type="button"
                  className={`rounded px-4 py-2 ${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(`/domiciliaria/${evaluadosList_id}`);
                  }}
                >
                  Salir
                </button>
              </div>
            )}
          </div>
          {/* Imprimir */}
          <div id="printableContent" style={{ display: "none" }}>
            <InfoFamiliarPrint />
          </div>
        </form>
      </div>
    </div>
  );
};
